import * as React from 'react';
// eslint-disable-next-line no-unused-vars
import { EventFormat } from '../../../../common/types/EventFormat';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { Colors } from '../../../../styles/Colors';
import DragAndDropHelper from '../../../../common/helpers/dragAndDropHelper';


const useStyles = makeStyles((theme) => ({
    draggable: {
        background: Colors.panel,
        borderRadius: 8,
        cursor: 'grab',
        padding: 8,
        margin: 4
    },
    droppable: {
        background: Colors.panelLight,
        color: Colors.panelLighterColor,
        borderRadius: 8,
        cursor: 'not-allowed',
        padding: 8,
        margin: 4
    },
    droppableDefined: {
        background: Colors.panel,
        color: Colors.pickstop,
        borderRadius: 8,
        cursor: 'not-allowed',
        padding: 8,
        margin: 4
    },
    subDrop: {
        marginLeft: 12
    },
    instructions: {
        opacity: 0.8,
        marginBottom: 8
    }
}));

/**
 * @typedef {Object} EventMapperProps
 * @property {any} details
 * @property {EventFormat} format
 */

/**
 * 
 * @param {EventMapperProps} param0
 * @returns
 */
export default function EventMapper({ details, format, onCreate }) {
    const classes = useStyles();
    let stageData = JSON.parse(format.json);
    const [state, setState] = React.useState({ stages: {} });

    React.useEffect(() => {
        if (Object.keys(state?.stages ?? {}).length !== stageData.stages.length) {
            let newState = {
                stages: {

                }
            };

            for (let i = 0; i < stageData.stages.length; i++) {
                newState.stages[i] = {
                    matchMappingKeys: [],
                    standingsMappingKeys: []
                }
            }

            setState(newState);
        }

    }, [details, format]);

    const onDragStart = (isMatch, stage, group) => {
        DragAndDropHelper.onDragStart({
            isMatch,
            stage,
            group
        });
    }

    const addToMapping = (list, key, index) => {
        while (list.length < index + 1) {
            list.push(null);
        }

        list[index] = key;
    }

    const onDrop = (stageIndex, substageIndex) => {
        const { isMatch, stage, group } = DragAndDropHelper.getDragData();

        const formatStage = stageData.stages[stageIndex];
        const targetMapping = state.stages[stageIndex];

        const matchMapping = {
            id: group.key,
            displayName: group.name,
            liquipediaPageId: stage.id,
            liquipediaPage: stage.name
        };
        const standingsMapping = {
            id: group.id,
            displayName: group.name,
            liquipediaPageId: stage.id,
            liquipediaPage: stage.name
        };

        // Group Stage
        if (formatStage.type === 1) {
            if (formatStage.groupStageDetails?.hasGroupBrackets) {
                if (isMatch) {
                    addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
                }
            }
            else {
                if (isMatch) {
                    addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
                }
                else {
                    addToMapping(targetMapping.standingsMappingKeys, standingsMapping, substageIndex)
                }
            }
        }
        // Swiss Stage
        else if (formatStage.type === 2) {
            if (isMatch) {
                addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
            }
            else {
                addToMapping(targetMapping.standingsMappingKeys, standingsMapping, substageIndex)
            }
        }
        else {
            if (isMatch) {
                addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
            }
        }

        setState({...state});
    }

    const onReset = () => {
        let newState = {
            stages: {

            }
        };

        for (let i = 0; i < stageData.stages.length; i++) {
            newState.stages[i] = {
                matchMappingKeys: [],
                standingsMappingKeys: []
            }
        }

        setState(newState);
    }

    let disableCreateEvent = false;
    const stageTypeName = (type) => (type === 1 ? 'Group' : type === 2 ? 'Swiss' : 'Playoffs');
    const eventStagesMapped = stageData.stages.map((stage, i) => {
        const subStages = state?.stages[i];
        let subStageCount = 1;

        // Group Stage
        if (stage.type === 1) {
            subStageCount = stage.groupStageDetails?.groupCount ?? 1;
        }
        // Swiss or Playoffs
        else if (stage.type === 2 || stage.type === 3) {
            subStageCount = 1;
        }

        const stageElements = [];
        for (let j = 0; j < subStageCount; j++) {
            let defined = !!(subStages?.standingsMappingKeys[j]?.displayName ?? subStages?.matchMappingKeys[j]?.displayName);
            const isMatch = !((stage.type === 1 && !stage.groupStageDetails?.hasGroupBrackets) || stage.type === 2);
            const emptyLabel = isMatch ? 'Drop a Bracket here' : 'Drop Standings here';
            let stageDisplayName = subStages?.standingsMappingKeys[j]?.displayName ?? subStages?.matchMappingKeys[j]?.displayName ?? emptyLabel;
            stageElements.push(
                <div className={defined ? classes.droppableDefined : classes.droppable}
                    onDragOver={(e) => { e.preventDefault() }}
                    onDrop={() => onDrop(i, j)}>
                    {stageDisplayName}
                </div>)

            if (!defined) {
                disableCreateEvent = true;
            }

            // Need to specify match for the standing table
            if ((stage.type === 1 && !stage.groupStageDetails?.hasGroupBrackets) || stage.type === 2) {
                defined = !!(subStages?.matchMappingKeys[j]?.displayName);
                stageElements.push(
                    <div className={(defined ? classes.droppableDefined : classes.droppable) + ' ' + classes.subDrop}
                        onDragOver={(e) => { e.preventDefault() }}
                        onDrop={() => onDrop(i, j)}>
                        {subStages?.matchMappingKeys[j]?.displayName ?? 'Drop a Bracket here to set the start time'}
                    </div>)

                if (!defined) {
                    disableCreateEvent = true;
                }
            }
        }

        return <div>
            <label>{stage.name} ({stageTypeName(stage.type)})</label>
            {stageElements}
        </div>
    });

    return <>
        <p className={classes.instructions}>
            Drag an API Stage (right) onto the matching format slot (left). Each Playoffs stage takes one bracket;
            a Group stage takes one slot per group plus a bracket for its start time. A heading with two brackets
            (e.g. a re-seeded qualifier) shows one chip per bracket.
        </p>
        <Grid container>
            <Grid item xs={5}>
                <h1>Event Stages</h1>
                {
                    eventStagesMapped
                }
                <div></div>
            </Grid>
            <Grid item xs={7}><h1>API Stages</h1>
                {
                    details.stageDetails.map((stage) => {
                        return <div>
                            <label><a target="_blank" href={stage.link} rel="noreferrer">{stage.name}</a></label>
                            {
                                stage.standingGroups.map((group) => {
                                    return <div className={classes.draggable} draggable="true" onDragStart={() => onDragStart(false, stage, group)}>{group.name} - Standings</div>
                                })
                            }
                            {
                                stage.matchGroups.map((group) => {
                                    return <div className={classes.draggable} draggable="true" onDragStart={() => onDragStart(true, stage, group)}>{group.name} - Bracket</div>
                                })
                            }
                        </div>
                    })
                }
            </Grid>
        </Grid>
        <Button color="primary" onClick={onReset}>Reset Mappings</Button>
        <Button color="secondary" onClick={() => { onCreate(state); }} disabled={disableCreateEvent}>Create Event</Button>
    </>
}